/**
 * Web adapters for backend access decisions on mutation failures.
 *
 * The Empire service object returns blocker lists when a write is denied.
 * These helpers parse that payload and map remediation intent to web routes.
 * Proactive checks still use the lightweight email-verification status query
 * plus client placement until we add a dedicated service call without GraphQL preflight.
 *
 * Fail-open rule: if the status query throws, UX gates let the user continue.
 * Authoritative write denials still use remediation / try_again from Empire.
 */
import { extractAccessBlockersFromError } from '@/access/extractAccessBlockersFromError';
import { applyAccessRemediation } from '@/access/applyAccessRemediation';
import { buildVerifyEmailPath, currentUserIsSitter } from '@/apps/verifyEmail/helpers/paths';
import { getEmailVerificationStatus } from '@/apps/verifyEmail/helpers/emailVerificationStatus';
import {
  EmailVerificationMoment,
  getEmailVerificationPlacement,
  isFullSitterOnboardingPath,
  shouldRequireEmailVerificationNow,
} from '@/apps/verifyEmail/helpers/placementPolicy';
import { markEmailVerificationRequired } from '@/apps/verifyEmail/helpers/pendingEmailVerification';
import { EMAIL_UNVERIFIED_REASON } from '@/access/types';
import { redirect } from '@/helpers/redirects';

export type EmailVerificationGateDecision = { requireVerification: false } | { requireVerification: true; redirectTo: string };

export type EmailVerificationGateOutcome = 'proceed' | 'redirected' | 'blocked';

export type AccessPolicyDenialOptions = {
  beforeRedirect?: () => boolean;
  onPersistenceFailure?: () => void;
  onTryAgain?: (message: string) => void;
  onVerifyEmail?: () => boolean;
};

export type DecideEmailVerificationGateInput = {
  verified: boolean;
  isSitter: boolean;
  placement: string;
  moment: EmailVerificationMoment;
  returnPath?: string | null;
};

export function decideEmailVerificationGate(input: DecideEmailVerificationGateInput): EmailVerificationGateDecision {
  if (!shouldRequireEmailVerificationNow(input)) {
    return { requireVerification: false };
  }

  return {
    requireVerification: true,
    redirectTo: buildVerifyEmailPath(input.returnPath),
  };
}

export function handleAccessPolicyDenial(
  error: unknown,
  returnPath?: string | null,
  options: AccessPolicyDenialOptions = {}
): boolean {
  const blockers = extractAccessBlockersFromError(error);
  if (!blockers.length) {
    return false;
  }

  return applyAccessRemediation(blockers, {
    returnPath,
    beforeRedirect: options.beforeRedirect,
    onPersistenceFailure: options.onPersistenceFailure,
    onTryAgain: options.onTryAgain,
    onVerifyEmail: options.onVerifyEmail,
  });
}

export type ResolveEmailVerificationGateInput = {
  moment: EmailVerificationMoment;
  returnPath?: string | null;
  /** Page used for the express-exclusion override. Defaults to returnPath. */
  onboardingPath?: string | null;
  isSitter?: boolean;
  fetchVerified?: () => Promise<boolean>;
  fetchExcludeFromExperiment?: () => Promise<boolean>;
  getPlacement?: () => string;
};

export async function resolveEmailVerificationGate(
  input: ResolveEmailVerificationGateInput
): Promise<EmailVerificationGateDecision> {
  const isSitter = input.isSitter ?? currentUserIsSitter();
  if (!isSitter) {
    return { requireVerification: false };
  }

  let verified: boolean;
  let excludeFromExperiment = false;
  try {
    if (input.fetchVerified || input.fetchExcludeFromExperiment) {
      verified = input.fetchVerified ? await input.fetchVerified() : false;
      excludeFromExperiment = input.fetchExcludeFromExperiment ? await input.fetchExcludeFromExperiment() : false;
    } else {
      const status = await getEmailVerificationStatus();
      verified = status.verified;
      excludeFromExperiment = status.excludeFromEmailVerificationExperiment;
    }
  } catch (error) {
    // Fail-open: do not brick navigation / send / apply when status is unavailable.
    console.error('Error checking email verification status:', error);
    return { requireVerification: false };
  }

  // Express/invite path: skip GB assignment and UX gates until full onboarding starts.
  // Profile intro / care-type / profile steps force evaluation even before profile.begin!.
  // onboardingPath covers leaving those pages for a legacy destination such as IDV.
  const onboardingPath = input.onboardingPath ?? input.returnPath;
  if (excludeFromExperiment && !isFullSitterOnboardingPath(onboardingPath)) {
    return { requireVerification: false };
  }

  const placement = input.getPlacement ? input.getPlacement() : getEmailVerificationPlacement();
  return decideEmailVerificationGate({
    verified,
    isSitter,
    placement,
    moment: input.moment,
    returnPath: input.returnPath,
  });
}

export async function ensureEmailVerificationOrRedirect(
  moment: EmailVerificationMoment,
  returnPath?: string | null,
  beforeRedirect?: () => boolean,
  onboardingPath?: string | null
): Promise<EmailVerificationGateOutcome> {
  const decision = await resolveEmailVerificationGate({ moment, returnPath, onboardingPath });
  if (!decision.requireVerification) {
    return 'proceed';
  }

  if (beforeRedirect && !beforeRedirect()) {
    return 'blocked';
  }

  markEmailVerificationRequired(returnPath);
  redirect(decision.redirectTo);

  return 'redirected';
}

export { extractAccessBlockersFromError } from '@/access/extractAccessBlockersFromError';
export { EMAIL_UNVERIFIED_REASON as EMAIL_UNVERIFIED_ERROR_CODE };
